/**
 * Provider rankings: category definitions and the pure ordering rules behind
 * the public top ten lists.
 *
 * Everything here is pure TypeScript so it can be unit tested and shared by
 * the server function, the rebuild script and (later) any read surface.
 */

export type ProviderGroupKey =
  | "re_equity"
  | "re_debt"
  | "loans"
  | "funds_established"
  | "funds_emerging"
  | "funds_seeder";

/** Which directory preference array a category is matched against. */
export type PrefColumn =
  | "pref_real_estate_property"
  | "pref_loan_types"
  | "pref_investments_in_funds";

export type ProviderCategory = {
  groupKey: ProviderGroupKey;
  categoryKey: string;
  label: string;
  /** Array the focus measure is computed from and the tokens live in. */
  prefColumn: PrefColumn;
  /** Any one of these tokens qualifies the firm. */
  tokens: string[];
  /** Extra token that must also be present in pref_investments_in_funds. */
  fundsAlsoRequires?: string;
  /** Required token in pref_real_estate_equity_debt. */
  requiresCapitalType?: "RE-Equity" | "RE-Debt";
  /** Restrict to these tier1_archetype values. */
  restrictArchetypes?: readonly string[];
  /**
   * Neighbouring tokens in the same mandate family. Holding more than one is
   * a small signal that the firm really works in this part of the market.
   */
  relatedTokens?: readonly string[];
};

/* ------------------------------------------------------------------ */
/* Archetype gate                                                      */
/* ------------------------------------------------------------------ */

/** tier2_subtype values that mark a credit or lending business. */
export const CREDIT_SUBTYPES = [
  "Mezzanine",
  "BDC",
  "Specialty Finance",
  "Commercial Lender",
  "Mortgage Bank",
  "Merchant Bank",
  "Credit Union",
] as const;

/** tier2_subtype values that mark a real estate business. */
export const REAL_ESTATE_SUBTYPES = ["REIT"] as const;

/** tier2_subtype values that mark a fund investing programme. */
export const ALLOCATOR_SUBTYPES = [
  "Hedge FoF",
  "PE FoF",
  "Investment Consultant",
] as const;

const has = (list: readonly string[], v: string | null): boolean =>
  v != null && list.includes(v);

/**
 * Whether a firm's archetype belongs on a given family of lists. Applied
 * before any scoring, so a firm that carries the right mandate token but the
 * wrong business model never reaches the ordering step.
 */
export function passesArchetypeGate(
  groupKey: ProviderGroupKey,
  tier1: string | null,
  tier2: string | null,
): boolean {
  switch (groupKey) {
    case "re_equity":
      if (has(REAL_ESTATE_SUBTYPES, tier2)) return true;
      return [
        "Real Estate Investor",
        "Family Office",
        "Insurance",
        "Pension Fund",
        "Sovereign Wealth Fund",
        "Endowment & Foundation",
      ].includes(tier1 ?? "");
    case "re_debt":
      if (has(REAL_ESTATE_SUBTYPES, tier2) || has(CREDIT_SUBTYPES, tier2)) return true;
      return ["Bank / Broker-Dealer", "Specialty Lender", "Insurance"].includes(
        tier1 ?? "",
      );
    case "loans":
      if (has(CREDIT_SUBTYPES, tier2)) return true;
      return ["Bank / Broker-Dealer", "Specialty Lender"].includes(tier1 ?? "");
    case "funds_established":
    case "funds_emerging":
    case "funds_seeder":
      if (["Venture Capital", "Private Equity"].includes(tier1 ?? "")) return false;
      if (tier1 === "Asset Manager") return has(ALLOCATOR_SUBTYPES, tier2);
      return (ALLOCATOR_ARCHETYPES as readonly string[]).includes(tier1 ?? "");
    default:
      return true;
  }
}

/**
 * Allocator archetypes for the established fund lists. These are the firm
 * kinds that commit capital to third party funds. General partners that
 * merely manage a strategy (Private Equity, Venture Capital, Hedge Fund,
 * Real Estate Investor, Specialty Lender and so on) are excluded.
 */
export const ALLOCATOR_ARCHETYPES = [
  "Pension Fund",
  "Endowment & Foundation",
  "Insurance",
  "Fund of Funds",
  "Sovereign Wealth Fund",
  "Family Office",
  "Asset Manager",
] as const;

const RE_EQUITY: Array<[string, string, string[]]> = [
  ["multifamily", "Multifamily", ["Multifamily"]],
  ["office", "Office", ["Office"]],
  ["retail", "Retail", ["Retail"]],
  ["industrial", "Industrial", ["Industrial", "Warehouse"]],
  ["hotel", "Hotel and hospitality", ["Hotel"]],
  ["senior-housing", "Senior housing", ["SeniorHousing"]],
  ["residential", "Residential", ["Residential"]],
  ["self-storage", "Self Storage", ["Self Storage"]],
  [
    "distressed-re",
    "Distressed and special situations",
    ["Distressed RE"],
  ],
];

const RE_DEBT: Array<[string, string, string[]]> = [
  ["multifamily", "Multifamily", ["Multifamily"]],
  ["office", "Office", ["Office"]],
  ["retail", "Retail", ["Retail"]],
  ["industrial", "Industrial", ["Industrial", "Warehouse"]],
  ["hotel", "Hotel and hospitality", ["Hotel"]],
  ["residential", "Residential", ["Residential"]],
];

const LOANS: Array<[string, string, string[]]> = [
  ["asset-based-loans", "Asset based loans", ["Asset Based Loans"]],
  [
    "senior-cash-flow-term-loans",
    "Senior cash-flow term loans",
    ["CashFlowSeniorLoan"],
  ],
  ["working-capital-loans", "Working capital loans", ["Working Capital Loans"]],
  ["equipment-financing", "Equipment financing", ["Equipment Financing"]],
  ["bridge-loans", "Bridge loans", ["Bridge Loans"]],
  [
    "mezzanine-and-subordinated-debt",
    "Mezzanine and subordinated debt",
    ["Mezzanine Financing", "Subordinated Loans"],
  ],
  ["unitranche", "Unitranche", ["Unitranche"]],
];

const FUND_STRATEGIES: Array<[string, string, string[]]> = [
  ["venture-capital", "Venture capital", ["VC Funds"]],
  ["private-equity-buyout", "Private equity and buyout", ["PE-Funds", "LBO Funds"]],
  ["real-estate", "Real estate", ["RE-Funds"]],
  ["infrastructure", "Infrastructure", ["Infrastructure"]],
  ["real-assets", "Real assets", ["RealAssets"]],
  ["distressed", "Distressed and special situations", ["Distressed"]],
  ["hedge-funds", "Hedge funds", ["HF-Strategy"]],
  ["secondaries", "Secondaries", ["SecondaryFunds"]],
];

/** The four strategies that also get emerging manager and seeder lists. */
const CROSSED_STRATEGY_KEYS = new Set([
  "venture-capital",
  "private-equity-buyout",
  "real-estate",
  "hedge-funds",
]);

/** Neighbouring property tokens, by real estate category key. */
const RE_RELATED: Record<string, string[]> = {
  multifamily: ["Apartment", "Student Housing"],
  residential: ["Apartment", "Student Housing"],
  industrial: ["Warehouse"],
  "senior-housing": ["Medical"],
};
const RE_RELATED_GENERIC = ["RealEstate-All", "All Property Types"];

/** Neighbouring loan tokens shared by every lending category. */
const LOAN_RELATED = ["Commercial Loans", "MiddleMarketLending", "SponsorLending"];

/** Neighbouring fund tokens, by strategy key. */
const FUND_RELATED: Record<string, string[]> = {
  "venture-capital": ["Fund of VCs"],
  "private-equity-buyout": ["Fund of PEs"],
  "real-estate": ["Fund of REs"],
  "hedge-funds": ["Fund of HFs"],
  infrastructure: ["RealAssets"],
  "real-assets": ["Infrastructure"],
};

const reRelated = (key: string): string[] => [
  ...(RE_RELATED[key] ?? []),
  ...RE_RELATED_GENERIC,
];
const fundRelated = (key: string): string[] => FUND_RELATED[key] ?? [];

export const PROVIDER_CATEGORIES: ProviderCategory[] = [
  ...RE_EQUITY.map(([categoryKey, label, tokens]) => ({
    groupKey: "re_equity" as const,
    categoryKey,
    label,
    prefColumn: "pref_real_estate_property" as const,
    tokens,
    requiresCapitalType: "RE-Equity" as const,
    relatedTokens: reRelated(categoryKey),
  })),
  ...RE_DEBT.map(([categoryKey, label, tokens]) => ({
    groupKey: "re_debt" as const,
    categoryKey,
    label,
    prefColumn: "pref_real_estate_property" as const,
    tokens,
    requiresCapitalType: "RE-Debt" as const,
    relatedTokens: reRelated(categoryKey),
  })),
  ...LOANS.map(([categoryKey, label, tokens]) => ({
    groupKey: "loans" as const,
    categoryKey,
    label,
    prefColumn: "pref_loan_types" as const,
    tokens,
    relatedTokens: LOAN_RELATED,
  })),
  ...FUND_STRATEGIES.map(([categoryKey, label, tokens]) => ({
    groupKey: "funds_established" as const,
    categoryKey,
    label,
    prefColumn: "pref_investments_in_funds" as const,
    tokens,
    restrictArchetypes: ALLOCATOR_ARCHETYPES,
    relatedTokens: fundRelated(categoryKey),
  })),
  ...FUND_STRATEGIES.filter(([k]) => CROSSED_STRATEGY_KEYS.has(k)).map(
    ([categoryKey, label, tokens]) => ({
      groupKey: "funds_emerging" as const,
      categoryKey,
      label,
      prefColumn: "pref_investments_in_funds" as const,
      tokens,
      fundsAlsoRequires: "EmergingManagers",
      relatedTokens: fundRelated(categoryKey),
    }),
  ),
  ...FUND_STRATEGIES.filter(([k]) => CROSSED_STRATEGY_KEYS.has(k)).map(
    ([categoryKey, label, tokens]) => ({
      groupKey: "funds_seeder" as const,
      categoryKey,
      label,
      prefColumn: "pref_investments_in_funds" as const,
      tokens,
      fundsAlsoRequires: "Seeder",
      relatedTokens: fundRelated(categoryKey),
    }),
  ),
];

/** A list is published only when at least this many firms qualify. */
export const MIN_ELIGIBLE_FIRMS = 25;

/** Ten names per published list. */
export const LIST_SIZE = 10;

/**
 * A list carries a footnote when fewer than half of its ten firms have a
 * recent activity figure.
 */
export const ACTIVITY_FOOTNOTE_THRESHOLD = 0.5;

export function ruleText(label: string): string {
  return `Firms in the CapitalDart directory whose stated mandate includes ${label}, ordered by recent investment activity, then size.`;
}

export type RankableFirm = {
  directory_firm_id: string;
  firm_name: string;
  firm_type: string | null;
  tier2_subtype: string | null;
  hq_city: string | null;
  hq_state: string | null;
  hq_country: string | null;
  aum_usd_mm: number | null;
  activity_measure: number | null;
  /** How many tokens of the category's family the firm carries. */
  family_token_count: number;
};

function compareAum(a: RankableFirm, b: RankableFirm): number {
  const av = a.aum_usd_mm;
  const bv = b.aum_usd_mm;
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  return bv - av;
}

/**
 * Order the qualifying firms for one category. Ties fall through to firm name
 * so the same directory snapshot always produces the same list.
 */
export function rankFirms(firms: readonly RankableFirm[]): RankableFirm[] {
  return [...firms].sort((a, b) => {
    const av = a.activity_measure ?? 0;
    const bv = b.activity_measure ?? 0;
    if (av !== bv) return bv - av;
    // A firm covering more of the family is the better fit at equal activity.
    const af = Math.min(a.family_token_count, 3);
    const bf = Math.min(b.family_token_count, 3);
    if (af !== bf) return bf - af;
    const aum = compareAum(a, b);
    if (aum !== 0) return aum;
    return a.firm_name.localeCompare(b.firm_name);
  });
}

/** Loose name key used by the comparison helper. */
export function normalizeFirmName(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/\b(llc|l\.l\.c|lp|l\.p|inc|incorporated|ltd|limited|plc|corp|corporation|company|co|group|partners|capital|management|advisors|advisers)\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}
